/* The query table: cell colour rules, sorting, pagination.
   Cell rendering ported verbatim from artifact.html. Rows now arrive one page at a time
   from the server, which does the filtering, sorting and paging. */

import { expectedCtr } from "@oqc/classifier";
import { $, esc, fmt } from "../util.js";
import { bpill, spill, jpill } from "../colors.js";

/* ---------- cells ---------- */

function clicksCell(d, sc) {
  let style = "color:var(--muted)";
  if (d.clicks === 0 && d.imp >= 20) style = "color:var(--red);font-weight:700";
  else if (d.clicks >= sc.clk90 && d.clicks > 0) style = "color:var(--green);font-weight:800";
  else if (d.clicks >= sc.clk50 && d.clicks > 0) style = "color:var(--ink)";
  return '<td class="mono num" style="' + style + '">' + fmt(d.clicks) + "</td>";
}

function impCell(d, sc) {
  let style = "color:var(--muted)";
  if (d.imp >= sc.imp90) style = "color:var(--violet);font-weight:800";
  else if (d.imp >= sc.imp50) style = "color:var(--ink)";
  return '<td class="mono num" style="' + style + '">' + fmt(d.imp) + "</td>";
}

function ctrCell(d) {
  if (d.imp < 10) return '<td class="mono num" style="color:var(--muted)">' + d.ctr.toFixed(1) + "%</td>";
  const ratio = d.ctr / Math.max(0.1, expectedCtr(d.pos));
  const [c, bg] = ratio >= 1 ? ["var(--green)", "var(--green-soft)"] : ratio >= 0.5 ? ["var(--amber)", "var(--amber-soft)"] : ["var(--red)", "var(--red-soft)"];
  return '<td class="num"><span class="pill" style="color:' + c + ";background:" + bg + ';font-weight:700" title="vs ~' + expectedCtr(d.pos).toFixed(1) + '% expected at this position">' + d.ctr.toFixed(1) + "%</span></td>";
}

function confCell(d) {
  const c = d.conf >= 90 ? "var(--green)" : d.conf >= 70 ? "var(--amber)" : "var(--red)";
  return '<td class="mono num" style="color:' + c + ';font-weight:700">' + d.conf + "%</td>";
}

function pageCell(d) {
  /* No real landing page, but the wording overlaps one of the AI-feature pages. Marked
     with ~ because it is an inference from shared words, not a Google join. */
  if (!d.page && d.aiMatch) {
    const inferred = esc(d.aiMatch.replace(/^https?:\/\/[^/]+/, "") || "/");
    return '<td class="pg"><span class="np pgc" style="opacity:.85" title="Inferred by token overlap with your Generative AI pages — not a Search Console query-to-page join: ' +
      esc(d.aiMatch) + '">~ ' + inferred + "</span></td>";
  }
  if (!d.page) return '<td class="pg"><span class="np">—</span></td>';
  const path = esc(d.page.replace(/^https?:\/\/[^/]+/, "") || "/");
  const isUrl = /^https?:/.test(d.page);
  if (isUrl) return '<td class="pg"><a href="' + esc(d.page) + '" target="_blank" rel="noopener" onclick="event.stopPropagation()" title="' + esc(d.page) + '"><span class="pgc">' + path + "</span> ↗</a></td>";
  return '<td class="pg"><span class="np pgc" title="' + esc(d.page) + '">' + path + "</span></td>";
}

function posCell(d) {
  const [c, bg] = d.pos <= 3 ? ["var(--green)", "var(--green-soft)"] : d.pos <= 5 ? ["var(--blue)", "var(--blue-soft)"] : d.pos <= 10 ? ["var(--amber)", "var(--amber-soft)"] : ["var(--red)", "var(--red-soft)"];
  return '<td class="num"><span class="pill" style="color:' + c + ";background:" + bg + ';font-weight:700">' + d.pos.toFixed(1) + "</span></td>";
}

/* ---------- table ---------- */

export function renderTable(page, ctx) {
  const { rows, total, size, page: pgIdx } = page;
  const { sortKey, sortDir, hasAI, hasPages, scale } = ctx;
  const maxIdx = Math.max(0, Math.ceil(total / size) - 1);
  const start = pgIdx * size;
  const pgText = total
    ? "Showing " + fmt(start + 1) + "–" + fmt(Math.min(start + size, total)) + " of " + fmt(total) + " queries" +
      (maxIdx ? "  ·  page " + (pgIdx + 1) + " of " + (maxIdx + 1) : "")
    : "No matching queries";

  /* Both pagination bars always say the same thing. */
  ["", "-b"].forEach(sfx => {
    $("pg-info" + sfx).textContent = pgText;
    $("pg-prev" + sfx).disabled = pgIdx === 0;
    $("pg-next" + sfx).disabled = pgIdx >= maxIdx;
    $("pg-size" + sfx).value = String(size);
  });

  const ar = k => (sortKey === k ? (sortDir === "desc" ? " ↓" : " ↑") : "");
  $("tbl").innerHTML = "<thead><tr><th>Query</th><th>Bucket</th><th>Stage</th><th>Journey</th>" +
    '<th class="num" data-s="conf">Conf' + ar("conf") + "</th>" +
    (hasAI ? '<th class="ctr">AI<br>page</th>' : "") +
    (hasPages ? "<th>Page</th>" : "") +
    '<th class="num" data-s="clicks">Clicks' + ar("clicks") + '</th><th class="num" data-s="imp">Impr' + ar("imp") +
    '</th><th class="num" data-s="ctr">CTR' + ar("ctr") + '</th><th class="num" data-s="pos">Pos' + ar("pos") +
    "</th></tr></thead><tbody>" +
    (rows.length ? rows.map(d =>
      '<tr class="r" data-qi="' + d.idx + '"><td class="q"><div class="qc" title="' + esc(d.q) + '">' + esc(d.q) + "</div></td><td>" +
      bpill(d.bucket) + "</td><td>" + spill(d.stage) + "</td><td>" + jpill(d.journey) + "</td>" + confCell(d) +
      (hasAI ? '<td class="ctr">' + (d.aiPage ? '<span class="check">✓</span>' : "") + "</td>" : "") +
      (hasPages ? pageCell(d) : "") +
      clicksCell(d, scale) + impCell(d, scale) + ctrCell(d) + posCell(d) + "</tr>"
    ).join("") : '<tr><td colspan="11" style="padding:30px;text-align:center"><b>No queries match</b><div style="font-size:12px;color:var(--muted);margin-top:3px">Clear the search or filters.</div></td></tr>') +
    "</tbody>";

  $("tbl").querySelectorAll("th[data-s]").forEach(th => th.addEventListener("click", () => ctx.onSort(th.dataset.s)));
  $("tbl").querySelectorAll("[data-qi]").forEach(tr => tr.addEventListener("click", () => ctx.onOpenRow(Number(tr.dataset.qi))));
}

export function scrollTableTop() {
  const el = document.querySelector(".pgbar.top");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}
